import { httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ReportingSsrmQueryDto } from '@api-dto-types/reporting-ssrm-query.dto';
import { ForecastVersionResponse } from '@api-response-types/forecast-version.response';
import {
  ReportingInitResponse,
  ReportingSsrmResponse,
} from '@api-response-types/reporting-ssrm.response';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { ReportingResultSetStatus } from '@api-types/enums/reporting-result-set-status.enum';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { selectConfigurationId } from 'demand-forecasting/store/router-state/router.selectors';
import { ReportingImportSseEventPayload } from '@api-types/sse/event-payloads/reporting-import.sse-event-payload';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  firstValueFrom,
  tap,
  throwError,
} from 'rxjs';
import { ReportingHttpClient } from './http/reporting-http-client';
import { ReportingImportEventService } from './reporting-import-event.service';

@Injectable()
export class ReportingStateService {
  private readonly store = inject(Store);
  private readonly reportingHttp = inject(ReportingHttpClient);
  private readonly importEventService = inject(ReportingImportEventService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private loadReportingSubscription: Subscription | null = null;
  private importEventSubscription: Subscription | null = null;
  private readonly configurationId = this.store.selectSignal(
    selectConfigurationId,
  );

  constructor() {
    this.importEventSubscription = this.importEventService.events$.subscribe(
      (payload) => this.onImportFinished(payload),
    );
  }

  // All versions for the configuration (GET)
  public versions = httpResource<ForecastVersionResponse[]>(() => {
    const configurationId = this.configurationId();
    if (configurationId === undefined) {
      return undefined;
    }
    return `${environment.api.url}/results/of-configuration/${configurationId}`;
  });

  // Only finished versions with reporting data ready
  public reportingReadyVersions = computed(() => {
    if (!this.versions.hasValue()) return [];
    return this.versions
      .value()
      .filter(
        (v) =>
          v.status === ForecastVersionStatus.FINISHED &&
          v.reportingStatus === ReportingResultSetStatus.READY,
      );
  });

  // Versions that can be imported (finished but not yet ready or importing)
  public importableVersions = computed(() => {
    if (!this.versions.hasValue()) return [];
    return this.versions
      .value()
      .filter(
        (v) =>
          v.status === ForecastVersionStatus.FINISHED &&
          v.reportingStatus !== ReportingResultSetStatus.READY &&
          v.reportingStatus !== ReportingResultSetStatus.IMPORTING,
      );
  });

  // User selections
  public selectedVersionIds = signal<string[]>([]);
  public selectedTimeSeries = signal<string[]>([]);
  public minDate = signal<string | undefined>(undefined);
  public maxDate = signal<string | undefined>(undefined);

  public reportingMeta = signal<ReportingInitResponse | null>(null);
  public isLoadingReportingData = signal(false);
  public reportingDataError = signal<string | null>(null);

  // Import status — local signal for immediate feedback + versions data for persistence across navigation
  private readonly importTriggered = signal(false);
  public isImportInProgress = computed(() => {
    if (this.importTriggered()) return true;
    if (!this.versions.hasValue()) return false;
    return this.versions
      .value()
      .some((v) => v.reportingStatus === ReportingResultSetStatus.IMPORTING);
  });
  public lastImportResult = signal<ReportingImportSseEventPayload | null>(null);

  // Auto-select newest version on first load
  private readonly autoSelectEffect = effect(() => {
    const readyVersions = this.reportingReadyVersions();
    if (readyVersions.length === 0) return;
    if (this.selectedVersionIds().length > 0) return;

    const newest = readyVersions.reduce((a, b) =>
      new Date(b.createdAt) > new Date(a.createdAt) ? b : a,
    );
    this.selectedVersionIds.set([newest.id]);
  });

  // Auto-load when any input changes (debounced)
  private autoLoadTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly autoLoadEffect = effect(() => {
    const versionIds = this.selectedVersionIds();
    // Track these signals to trigger reload on change
    this.selectedTimeSeries();
    this.minDate();
    this.maxDate();

    if (versionIds.length === 0) return;

    if (this.autoLoadTimer) clearTimeout(this.autoLoadTimer);
    this.autoLoadTimer = setTimeout(() => this.loadReportingData(), 500);
  });

  public loadReportingData(): void {
    const configurationId = this.configurationId();
    const versionIds = this.selectedVersionIds();

    if (!configurationId || versionIds.length === 0) {
      return;
    }

    this.isLoadingReportingData.set(true);
    this.reportingDataError.set(null);

    const body: {
      forecastVersionIds: string[];
      additionalTimeSeries?: string[];
      minDate?: string;
      maxDate?: string;
    } = {
      forecastVersionIds: versionIds,
    };

    const additionalTs = this.selectedTimeSeries();
    if (additionalTs.length > 0) {
      body.additionalTimeSeries = additionalTs;
    }

    const min = this.minDate();
    if (min) {
      body.minDate = min;
    }

    const max = this.maxDate();
    if (max) {
      body.maxDate = max;
    }

    this.loadReportingSubscription?.unsubscribe();

    this.loadReportingSubscription = this.reportingHttp
      .initReportingData(configurationId, body)
      .subscribe({
        next: (data) => {
          this.reportingMeta.set(data);
          this.isLoadingReportingData.set(false);
        },
        error: (err) => {
          console.error('Failed to load reporting data:', err);
          this.reportingDataError.set(
            'Failed to load reporting data. Please try again.',
          );
          this.isLoadingReportingData.set(false);
        },
      });
  }

  public queryRows(
    request: Omit<
      ReportingSsrmQueryDto,
      | 'resultSetIds'
      | 'newestResultSetId'
      | 'resultSetVersionLabels'
      | 'metadata'
      | 'minDate'
      | 'maxDate'
    >,
  ): Promise<ReportingSsrmResponse> {
    const configurationId = this.configurationId();
    if (!configurationId) {
      throw new Error('Configuration ID is required for querying rows');
    }
    const meta = this.reportingMeta();

    if (!meta) {
      throw new Error('Reporting metadata is required for querying rows');
    }

    const body = {
      ...request,
      resultSetIds: meta.resultSetIds,
      newestResultSetId: meta.newestResultSetId,
      resultSetVersionLabels: meta.resultSetVersionLabels,
      metadata: meta.metadata,
      minDate: meta.minDate,
      maxDate: meta.maxDate,
    };

    return firstValueFrom(this.reportingHttp.queryRows(configurationId, body));
  }

  public resetState(): void {
    if (this.autoLoadTimer) clearTimeout(this.autoLoadTimer);
    this.autoLoadTimer = null;
    this.loadReportingSubscription?.unsubscribe();
    this.loadReportingSubscription = null;
    this.importEventSubscription?.unsubscribe();
    this.importEventSubscription = null;
    this.reportingMeta.set(null);
    this.selectedVersionIds.set([]);
    this.selectedTimeSeries.set([]);
    this.minDate.set(undefined);
    this.maxDate.set(undefined);
    this.reportingDataError.set(null);
    this.lastImportResult.set(null);
    this.importTriggered.set(false);
  }

  public triggerImport(
    forecastVersionId: string,
  ): Observable<{ message: string }> {
    const configurationId = this.configurationId();
    if (!configurationId) return EMPTY;

    this.lastImportResult.set(null);
    this.importTriggered.set(true);

    return this.reportingHttp
      .triggerImport(configurationId, forecastVersionId)
      .pipe(
        tap(() => this.versions.reload()),
        catchError((error) => {
          this.importTriggered.set(false);
          return throwError(() => error);
        }),
      );
  }

  public onImportFinished(payload: ReportingImportSseEventPayload): void {
    this.lastImportResult.set(payload);
    this.importTriggered.set(false);
    this.versions.reload();

    if (payload.status === ReportingResultSetStatus.READY) {
      this.toastrService.success(
        this.translateService.instant('REPORTING.IMPORT_SUCCESS'),
      );
    } else {
      this.toastrService.error(
        payload.errorMessage ??
          this.translateService.instant('REPORTING.IMPORT_FAILED'),
      );
    }
  }
}
